import { useEffect, useState, useRef } from 'react';

export default function SpotsRemaining() {
  const [animated, setAnimated] = useState(false);
  const [progress, setProgress] = useState(0);
  const sectionRef = useRef<HTMLDivElement>(null);

  const spotsClaimed = 347;
  const totalSpots = 500;
  const spotsRemaining = totalSpots - spotsClaimed;
  const progressPercent = (spotsClaimed / totalSpots) * 100;

  useEffect(() => {
    const observer = new IntersectionObserver(
      ([entry]) => {
        if (entry.isIntersecting) {
          setAnimated(true);
        }
      },
      { threshold: 0.2 }
    );

    if (sectionRef.current) {
      observer.observe(sectionRef.current);
    }

    return () => observer.disconnect();
  }, []);

  useEffect(() => {
    if (animated) {
      const duration = 2000;
      const steps = 60;
      const stepTime = duration / steps;

      let currentStep = 0;
      const interval = setInterval(() => {
        currentStep++;
        const currentProgress = (progressPercent * currentStep) / steps;
        setProgress(currentProgress);

        if (currentStep >= steps) {
          clearInterval(interval);
          setProgress(progressPercent);
        }
      }, stepTime);

      return () => clearInterval(interval);
    }
  }, [animated, progressPercent]);

  return (
    <section ref={sectionRef} className="relative py-16 sm:py-20 lg:py-32 bg-gradient-to-b from-[#0a0a0c] to-[#09090b]">
      <div className="max-w-[900px] mx-auto px-4 sm:px-6 lg:px-8">
        <div
          className={`p-6 sm:p-8 lg:p-12 bg-gradient-to-b from-[#d4af37]/20 to-[#0f0f11] border-2 border-[#d4af37] rounded-2xl transition-all duration-700 ${
            animated ? 'opacity-100 scale-100' : 'opacity-0 scale-95'
          }`}
        >
          <div className="text-center space-y-6 sm:space-y-8">
            <h2 className="text-white text-[24px] sm:text-[32px] lg:text-[40px] font-black">Spots Remaining</h2>

            <div className="space-y-3 sm:space-y-4">
              <div className="flex items-center justify-center gap-2 sm:gap-3">
                <span className="text-[32px] sm:text-[48px] lg:text-[56px]">🏆</span>
                <div className="text-left">
                  <p className="text-[#d4af37] text-[28px] sm:text-[36px] lg:text-[48px] font-black leading-none">
                    {spotsRemaining}
                  </p>
                  <p className="text-[#a1a1aa] text-xs sm:text-sm lg:text-base font-semibold">of 500 remaining</p>
                </div>
              </div>

              <div className="relative w-full h-4 sm:h-6 bg-[#1a1a1c] rounded-full overflow-hidden">
                <div
                  className="absolute left-0 top-0 h-full bg-gradient-to-r from-[#d4af37] to-[#f4d03f] transition-all duration-2000 ease-out"
                  style={{ width: `${progress}%` }}
                ></div>
              </div>

              <p className="text-[#d4d4d8] text-xs sm:text-sm lg:text-base">
                <span className="text-[#d4af37] font-bold">{spotsClaimed}</span> claimed • <span className="text-white font-bold">{spotsRemaining}</span> left
              </p>
            </div>

            <p className="text-white text-sm sm:text-base lg:text-lg font-bold italic">
              When founder spots are gone, they're gone. No exceptions
            </p>
          </div>
        </div>
      </div>
    </section>
  );
}
